import { json } from './json'
import { crypt } from './crypt'

interface SessionStorage {
  get<T>(key: string, nestedKeyPath?: string, passphrase?: string): T | null
  set<T>(key: string, value: T, nestedKeyPath?: string, passphrase?: string): void
  remove(key: string): void
}

function getData(key: string, passphrase?: string): any {
  const storedData = sessionStorage.getItem(key)
  if (!storedData) return null
  const payload =
    passphrase && crypt.isHexString(storedData)
      ? (() => {
          try {
            return crypt.decrypt(passphrase, storedData)
          } catch {
            return storedData
          }
        })()
      : storedData
  const parsed = json.parse(payload) ?? json.parse(storedData)
  if (parsed === null) {
    const trimmed = storedData.trim()
    const looksLikeJSON = trimmed.startsWith('{') || trimmed.startsWith('[') || trimmed.startsWith('"')
    if (looksLikeJSON) {
      try {
        sessionStorage.removeItem(key)
      } catch {}
    }
    return null
  }
  return parsed
}

function saveData(key: string, data: any, passphrase?: string): void {
  const serializedData = json.stringify(data)
  if (serializedData === null) {
    try {
      sessionStorage.removeItem(key)
    } catch {}
    return
  }
  const encryptedData = (() => {
    if (!passphrase) return serializedData
    try {
      return crypt.encrypt(passphrase, serializedData)
    } catch {
      return serializedData
    }
  })()
  try {
    sessionStorage.setItem(key, encryptedData)
  } catch {
    try {
      sessionStorage.removeItem(key)
    } catch {}
  }
}

function setOrUpdateNestedValueAtPath(data: any, nestedKeyPath: string, value: any): void {
  const keys = nestedKeyPath.split('.')
  let obj = data
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i]
    if (!key) continue
    if (obj[key] == null || (typeof obj[key] !== 'object' && typeof obj[key] !== 'function')) {
      obj[key] = {}
    }
    obj = obj[key]
  }
  const lastKey = keys[keys.length - 1]
  if (lastKey) {
    obj[lastKey] = value
  }
}

function getNestedValueAtPath(data: any, nestedKeyPath: string): any {
  const keys = nestedKeyPath.split('.')
  let obj = data
  for (const key of keys) {
    if (!key) return null
    if (obj == null || (typeof obj !== 'object' && typeof obj !== 'function')) return null
    if (!Object.prototype.hasOwnProperty.call(obj, key)) return null
    obj = obj[key]
  }
  return obj
}

export const session: SessionStorage = {
  get<T>(key: string, nestedKeyPath?: string, passphrase?: string): T | null {
    const storedData = getData(key, passphrase)

    if (!nestedKeyPath) {
      return storedData || null
    }

    return storedData ? getNestedValueAtPath(storedData, nestedKeyPath) : null
  },

  set<T>(key: string, value: T, nestedKeyPath?: string, passphrase?: string): void {
    let currentValue: T
    const existing = getData(key, passphrase)
    if (!existing) {
      currentValue = nestedKeyPath ? ({} as T) : (undefined as unknown as T)
    } else {
      currentValue = existing as T
    }

    if (!nestedKeyPath) {
      currentValue = value
      if (currentValue === undefined || currentValue === null) {
        this.remove(key)
        return
      }
    } else {
      const base: any = currentValue == null || typeof currentValue !== 'object' ? {} : currentValue
      setOrUpdateNestedValueAtPath(base, nestedKeyPath, value)
      currentValue = base as T
    }

    if (Array.isArray(currentValue)) {
      currentValue = currentValue.filter((entry) => entry !== null && entry !== undefined) as T
    }

    saveData(key, currentValue, passphrase)
  },

  remove(key: string): void {
    sessionStorage.removeItem(key)
  }
}
